import { useEffect, useRef } from 'react';
import { disconnectSocket } from '../services/socket';

const IDLE_LOGOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
const SCHEDULED_LOGOUT_HOUR = 3; // force logout at 3 AM local time, regardless of activity
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

const msUntilNextScheduledHour = (hour: number) => {
  const now = new Date();
  const next = new Date(now);
  next.setHours(hour, 0, 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  return next.getTime() - now.getTime();
};

// Auto-logs the user out after a stretch of inactivity on the CRM; any tracked
// activity event pushes the deadline back out, so only a continuous idle
// stretch of `timeoutMs` triggers the logout. Independently of that, a daily
// timer forces a logout at `SCHEDULED_LOGOUT_HOUR` even if the user is active.
const useIdleLogout = (timeoutMs: number = IDLE_LOGOUT_MS) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const logout = () => {
      disconnectSocket();
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('siderMenuData');
        sessionStorage.clear();
      } catch {
        /* ignore */
      }
      window.location.replace('/login');
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, timeoutMs);
    };

    resetTimer();
    // `scroll` doesn't bubble, so a capture-phase listener is needed to see
    // scrolling inside nested containers (e.g. the main content panel).
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer, { capture: true }));

    // Runs independently of activity — fires once the clock hits 3 AM even
    // if the idle timer above keeps getting reset.
    scheduledTimerRef.current = setTimeout(logout, msUntilNextScheduledHour(SCHEDULED_LOGOUT_HOUR));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (scheduledTimerRef.current) clearTimeout(scheduledTimerRef.current);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer, { capture: true }));
    };
  }, [timeoutMs]);
};

export default useIdleLogout;
